import { ContactsList } from '@dev/ee-api/ee-api-models';
import { DialogBody } from '@dev/ee-components';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
    useSelectedEntity,
    useEntityRuleForFiltration,
    useContactsCount,
    useContactQueryParams,
} from '@/components/Contacts/Group/hooks';
import { DEFAULT_PER_PAGE_COUNT } from '@/services/constants';
import { calculateActivePage, calculateOffset } from '@/services/tools/handleUrlParams';
import { ShowDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import { ListRemoveContactsStart } from '@/store/lists/actions';
import { ContactTableContext } from '../../context';

export const useRemoveFromList = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { selectedEntity } = useSelectedEntity();
    const { selectedRows, selectedAll, setSelectedRows, setSelectedAll } = useContext(ContactTableContext);
    const { filtrationRuleWithQuery } = useEntityRuleForFiltration();
    const contactsCount = useContactsCount();
    const { queryPage, queryStatuses, pushParamToUrl } = useContactQueryParams();
    const removeEmailsFromList = (email?: string): void => {
        const emailsToRemove = !!email ? [email] : selectedRows;
        const removedContacts = {
            emails: selectedAll ? null : emailsToRemove,
            rule: selectedAll ? filtrationRuleWithQuery : null,
        };
        const emailsNumber = selectedAll ? contactsCount : emailsToRemove.length;
        const countAfterRemove = contactsCount - emailsNumber;
        const limit = DEFAULT_PER_PAGE_COUNT;
        const offset = limit * (queryPage - 1);
        const activePage = countAfterRemove > limit ? calculateActivePage(offset, limit, countAfterRemove) : 1;
        const offsetAfterRemove = calculateOffset(activePage, limit, countAfterRemove);
        const statuses = selectedAll ? null : queryStatuses;
        const params: ShowDialogParams = {
            title: `${t('remove')} ${emailsNumber} ${emailsNumber > 1 ? t('contacts') : t('contact')}`,
            destructiveLabel: t('remove'),
            onClickDestructive: (): void => {
                dispatch(
                    ListRemoveContactsStart(
                        (selectedEntity as ContactsList)?.ListName,
                        removedContacts,
                        limit,
                        offsetAfterRemove,
                        statuses
                    )
                );
                setSelectedRows([]);
                setSelectedAll(false);
                pushParamToUrl({ statuses });
            },
            cancelLabel: t('cancel'),
            body: (
                <DialogBody variant="alert" above={t('removefromlistdesc')}>
                    {t('deletecontactsstaymsg')}
                </DialogBody>
            ),
        };

        dispatch(ShowDialog(params));
    };

    return { removeEmailsFromList };
};
